import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, FingerprintChip } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  Mic, Square, Paperclip, Send, X, AudioLines, Fingerprint, ChevronRight,
  ChevronLeft, EyeOff, ShieldAlert, AlertTriangle, Check, PenLine, ClipboardCheck,
  Calendar as CalendarIcon, MapPin, Repeat, Gauge, Target, History, User as UserIcon,
} from "lucide-react";

const CATEGORIES = [
  "Harassment", "Discrimination", "Workplace Safety", "Compensation",
  "Policy Violation", "Interpersonal Conflict", "Performance",
  "Benefits", "Ethics", "Whistleblower", "Other",
];

const RECURRENCE_OPTIONS = [
  { value: "first_time", label: "This is the first time", desc: "A single, isolated incident" },
  { value: "repeated", label: "It has happened before", desc: "Pattern of similar incidents" },
  { value: "escalating", label: "The behavior is escalating", desc: "Getting worse over time" },
  { value: "ongoing", label: "It is ongoing", desc: "Currently happening on a regular basis" },
];

const IMPACT_OPTIONS = [
  { value: "minimal", label: "Minimal", desc: "Passing discomfort" },
  { value: "moderate", label: "Moderate", desc: "Affects daily work" },
  { value: "significant", label: "Significant", desc: "Affects wellbeing / performance" },
  { value: "severe", label: "Severe", desc: "Safety / legal concern" },
];

const STEPS = [
  { key: "category", title: "Classify", label: "Category & Priority" },
  { key: "details", title: "Describe", label: "Details" },
  { key: "context", title: "Context", label: "When & Where" },
  { key: "evidence", title: "Evidence", label: "Files & Voice" },
  { key: "consent", title: "Confidentiality", label: "Anonymity & Legal" },
  { key: "review", title: "Review", label: "Sign & Submit" },
];

export default function CreateCase() {
  const { user } = useAuth();
  const nav = useNavigate();

  // Wizard state
  const [step, setStep] = useState(0);
  const [showAnonDisclaimer, setShowAnonDisclaimer] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Interpersonal Conflict",
    priority: "medium",
    is_anonymous: false,
    incident_date: "",
    location: "",
    recurrence: "first_time",
    impact_severity: "moderate",
    requested_outcome: "",
    prior_reports: "",
    involved: "",
    witnesses: "",
    tags: "",
    acknowledged_truthful: false,
    legal_signature: "",
  });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [uploadReason, setUploadReason] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);

  const isAnon = form.is_anonymous;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!uploadReason.trim()) {
      toast.error("Please provide a reason for the upload first");
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "file");
        fd.append("is_anonymous_upload", String(isAnon));
        fd.append("reason", uploadReason);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((p) => [...p, data]);
        toast.success(`Uploaded ${file.name}`);
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const startRec = async () => {
    if (!uploadReason.trim()) return toast.error("Please provide a reason for the upload first");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "voice_note");
        fd.append("is_anonymous_upload", String(isAnon));
        fd.append("reason", uploadReason);
        setUploading(true);
        try {
          const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((p) => [...p, data]);
          toast.success("Voice note transcribed & attached");
        } catch { toast.error("Voice upload failed"); }
        finally { setUploading(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setRecTime(0);
      timerRef.current = setInterval(() => setRecTime((t) => t + 1), 1000);
    } catch { toast.error("Microphone permission denied"); }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  // Anonymous toggle with disclaimer gate
  const toggleAnon = (want) => {
    if (want) {
      setShowAnonDisclaimer(true);
    } else {
      setF("is_anonymous", false);
    }
  };

  const acceptAnonDisclaimer = () => {
    setF("is_anonymous", true);
    setShowAnonDisclaimer(false);
    toast.success("Anonymous mode enabled");
  };

  // Step validation
  const canProceed = useMemo(() => {
    switch (step) {
      case 0: return !!form.category && !!form.priority;
      case 1: return form.title.trim().length >= 4 && form.description.trim().length >= 20;
      case 2: return true; // context step is optional
      case 3: return true; // evidence step is optional
      case 4: return true; // consent selection screen
      case 5:
        if (!form.acknowledged_truthful) return false;
        if (isAnon && form.legal_signature.trim().length < 3) return false;
        if (isAnon && form.legal_signature.trim().toLowerCase() !== (user?.name || "").trim().toLowerCase()) return false;
        return true;
      default: return true;
    }
  }, [step, form, isAnon, user]);

  const next = () => {
    if (!canProceed) {
      toast.error("Please complete the required fields");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        priority: form.priority,
        is_anonymous: form.is_anonymous,
        involved_parties: form.involved.split(",").map(s => s.trim()).filter(Boolean),
        witnesses: form.witnesses.split(",").map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        attachment_ids: attachments.map((a) => a.id),
        recurrence: form.recurrence,
        incident_date: form.incident_date || null,
        location: form.location || null,
        impact_severity: form.impact_severity,
        requested_outcome: form.requested_outcome || null,
        prior_reports: form.prior_reports || null,
        acknowledged_truthful: form.acknowledged_truthful,
        legal_signature: form.is_anonymous ? form.legal_signature.trim() : null,
      };
      const { data } = await api.post("/cases", payload);
      toast.success(`Case ${data.case_number} created`);
      nav(`/cases/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create case");
    } finally { setSubmitting(false); }
  };

  const shellCls = isAnon
    ? "incognito bg-slate-950 text-slate-100"
    : "bg-white text-slate-900";

  return (
    <div className={"min-h-full transition-colors " + shellCls} data-testid="create-case-wizard">
      <AnimatePresence>
        {showAnonDisclaimer && (
          <AnonymousDisclaimerModal
            onAccept={acceptAnonDisclaimer}
            onCancel={() => setShowAnonDisclaimer(false)}
          />
        )}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-6 md:px-8 py-8">
        <SectionHeader
          title={isAnon ? "Anonymous Report" : "Report a Case"}
          subtitle="PropHR AI will summarize, classify, and route your report. Every step is fingerprinted for audit."
        />

        {/* Stepper */}
        <Stepper step={step} steps={STEPS} isAnon={isAnon} />

        {/* Panels */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="mt-6"
            data-testid={TID.wizardStep(step)}
          >
            {step === 0 && (
              <StepCategory form={form} setF={setF} isAnon={isAnon} />
            )}
            {step === 1 && (
              <StepDetails form={form} setF={setF} isAnon={isAnon} />
            )}
            {step === 2 && (
              <StepContext form={form} setF={setF} isAnon={isAnon} />
            )}
            {step === 3 && (
              <StepEvidence
                attachments={attachments}
                uploadReason={uploadReason}
                setUploadReason={setUploadReason}
                fileInputRef={fileInputRef}
                handleFiles={handleFiles}
                startRec={startRec}
                stopRec={stopRec}
                recording={recording}
                recTime={recTime}
                uploading={uploading}
                removeAttachment={removeAttachment}
                isAnon={isAnon}
              />
            )}
            {step === 4 && (
              <StepConsent
                form={form}
                setF={setF}
                toggleAnon={toggleAnon}
                isAnon={isAnon}
              />
            )}
            {step === 5 && (
              <StepReview
                form={form}
                user={user}
                setF={setF}
                attachments={attachments}
                isAnon={isAnon}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Footer nav */}
        <div className={"mt-8 pt-5 border-t flex items-center justify-between " + (isAnon ? "border-slate-800" : "border-slate-200")}>
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className={"btn-ghost disabled:opacity-40 disabled:cursor-not-allowed " + (isAnon ? "!text-slate-300 hover:!bg-slate-800" : "")}
            data-testid={TID.wizardBack}
          >
            <ChevronLeft size={14} strokeWidth={2}/> Back
          </button>
          <div className={"text-[11px] font-mono " + (isAnon ? "text-slate-500" : "text-slate-500")}>
            STEP {step + 1} / {STEPS.length}
          </div>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              disabled={!canProceed}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={TID.wizardNext}
            >
              Continue <ChevronRight size={14} strokeWidth={2}/>
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canProceed || submitting}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={TID.createCaseSubmit}
            >
              {submitting ? "Submitting…" : "Sign & Submit"} <Send size={14} strokeWidth={2}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   STEPPER
   ============================================================================ */
function Stepper({ step, steps, isAnon }) {
  return (
    <div className="mt-4 mb-2">
      {/* Compact progress bar */}
      <div className={"h-1.5 mb-4 " + (isAnon ? "bg-slate-800" : "bg-slate-100")}>
        <motion.div
          className={isAnon ? "h-full bg-blue-400" : "h-full bg-blue-600"}
          initial={{ width: 0 }}
          animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
      </div>
      {/* Wrap grid: 2 columns on mobile, 3 on tablet, all steps on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {steps.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2 min-w-0">
              <motion.div
                initial={false}
                animate={{ scale: active ? 1.05 : 1 }}
                className={
                  "w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold border " +
                  (active
                    ? (isAnon ? "bg-blue-500 text-white border-blue-500" : "bg-blue-600 text-white border-blue-600")
                    : done
                      ? (isAnon ? "bg-slate-700 text-slate-100 border-slate-700" : "bg-slate-900 text-white border-slate-900")
                      : (isAnon ? "bg-slate-900 text-slate-400 border-slate-700" : "bg-white text-slate-500 border-slate-300"))
                }
              >
                {done ? <Check size={12} strokeWidth={3}/> : i + 1}
              </motion.div>
              <div className="flex flex-col leading-tight min-w-0">
                <span className={"text-[9px] font-mono uppercase tracking-wider truncate " + (isAnon ? "text-slate-500" : "text-slate-400")}>{s.title}</span>
                <span className={"text-xs font-medium truncate " + (active
                  ? (isAnon ? "text-blue-200" : "text-slate-900")
                  : (isAnon ? "text-slate-300" : "text-slate-600"))}>{s.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================================
   STEP 1 - Category & Priority
   ============================================================================ */
function StepCategory({ form, setF, isAnon }) {
  const priorities = [
    { value: "low", label: "Low", note: "General concern" },
    { value: "medium", label: "Medium", note: "Meaningful impact" },
    { value: "high", label: "High", note: "Urgent attention" },
    { value: "critical", label: "Critical", note: "Immediate action needed" },
  ];
  return (
    <div className={"space-y-6"}>
      <FieldBlock isAnon={isAnon} label="Category" hint="What kind of issue is this?">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setF("category", c)}
              className={
                "text-left px-3 py-2 text-sm border transition-all " +
                (form.category === c
                  ? (isAnon ? "bg-blue-500/10 border-blue-500 text-blue-200" : "bg-blue-50 border-blue-600 text-blue-900")
                  : (isAnon ? "bg-slate-900 border-slate-700 text-slate-200 hover:border-slate-500" : "bg-white border-slate-200 text-slate-800 hover:border-slate-400"))
              }
              data-testid={`create-case-cat-${c.toLowerCase().replaceAll(" ","-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </FieldBlock>

      <FieldBlock isAnon={isAnon} label="Priority" hint="How urgent is this?">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {priorities.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setF("priority", p.value)}
              className={
                "text-left p-3 border transition-all " +
                (form.priority === p.value
                  ? (isAnon ? "bg-blue-500/10 border-blue-500" : "bg-blue-50 border-blue-600")
                  : (isAnon ? "bg-slate-900 border-slate-700 hover:border-slate-500" : "bg-white border-slate-200 hover:border-slate-400"))
              }
              data-testid={`create-case-pri-${p.value}`}
            >
              <div className={"font-semibold text-sm " + (isAnon ? "text-slate-100" : "text-slate-900")}>{p.label}</div>
              <div className={"text-[11px] mt-0.5 " + (isAnon ? "text-slate-400" : "text-slate-500")}>{p.note}</div>
            </button>
          ))}
        </div>
      </FieldBlock>
    </div>
  );
}

/* ============================================================================
   STEP 2 - Details
   ============================================================================ */
function StepDetails({ form, setF, isAnon }) {
  const inputCls = "input";
  return (
    <div className="space-y-5">
      <FieldBlock isAnon={isAnon} label="Case title" hint="A short summary (max 120 chars)">
        <input
          value={form.title}
          onChange={(e) => setF("title", e.target.value.slice(0, 120))}
          placeholder="e.g. Repeated unwelcome comments from a peer"
          className={inputCls}
          data-testid={TID.createCaseTitle}
        />
        <div className={"text-[11px] mt-1 font-mono " + (isAnon ? "text-slate-500" : "text-slate-400")}>
          {form.title.length}/120 · minimum 4 characters
        </div>
      </FieldBlock>

      <FieldBlock isAnon={isAnon} label="What happened?" hint="Facts, dates, quotes. Keep it factual , PropHR AI will summarize.">
        <textarea
          value={form.description}
          onChange={(e) => setF("description", e.target.value)}
          rows={7}
          placeholder="Describe what happened, when, and who was involved…"
          className={inputCls}
          data-testid={TID.createCaseDescription}
        />
        <div className={"text-[11px] mt-1 font-mono " + (isAnon ? "text-slate-500" : "text-slate-400")}>
          {form.description.length} characters · minimum 20
        </div>
      </FieldBlock>
    </div>
  );
}

/* ============================================================================
   STEP 3 - Context (when / where / recurrence / impact / prior reports)
   ============================================================================ */
function StepContext({ form, setF, isAnon }) {
  const inputCls = "input";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldBlock isAnon={isAnon} label="Incident date" icon={CalendarIcon}>
          <input
            type="date"
            value={form.incident_date}
            onChange={(e) => setF("incident_date", e.target.value)}
            className={inputCls}
            data-testid={TID.createCaseIncidentDate}
          />
        </FieldBlock>
        <FieldBlock isAnon={isAnon} label="Where did it happen?" icon={MapPin}>
          <input
            value={form.location}
            onChange={(e) => setF("location", e.target.value)}
            placeholder="Office, Slack, off-site, remote…"
            className={inputCls}
            data-testid={TID.createCaseLocation}
          />
        </FieldBlock>
      </div>

      <FieldBlock isAnon={isAnon} label="Is this the first time?" icon={Repeat} hint="Recurrence pattern helps HR prioritize">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {RECURRENCE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setF("recurrence", o.value)}
              data-testid={`${TID.createCaseRecurrence}-${o.value}`}
              className={
                "text-left p-3 border transition-all " +
                (form.recurrence === o.value
                  ? (isAnon ? "bg-blue-500/10 border-blue-500" : "bg-blue-50 border-blue-600")
                  : (isAnon ? "bg-slate-900 border-slate-700 hover:border-slate-500" : "bg-white border-slate-200 hover:border-slate-400"))
              }
            >
              <div className={"font-semibold text-sm " + (isAnon ? "text-slate-100" : "text-slate-900")}>{o.label}</div>
              <div className={"text-[11px] mt-0.5 " + (isAnon ? "text-slate-400" : "text-slate-500")}>{o.desc}</div>
            </button>
          ))}
        </div>
      </FieldBlock>

      <FieldBlock isAnon={isAnon} label="Impact severity" icon={Gauge}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {IMPACT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setF("impact_severity", o.value)}
              data-testid={`${TID.createCaseImpact}-${o.value}`}
              className={
                "text-left p-3 border transition-all " +
                (form.impact_severity === o.value
                  ? (isAnon ? "bg-blue-500/10 border-blue-500" : "bg-blue-50 border-blue-600")
                  : (isAnon ? "bg-slate-900 border-slate-700 hover:border-slate-500" : "bg-white border-slate-200 hover:border-slate-400"))
              }
            >
              <div className={"font-semibold text-sm " + (isAnon ? "text-slate-100" : "text-slate-900")}>{o.label}</div>
              <div className={"text-[11px] mt-0.5 " + (isAnon ? "text-slate-400" : "text-slate-500")}>{o.desc}</div>
            </button>
          ))}
        </div>
      </FieldBlock>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldBlock isAnon={isAnon} label="Involved parties" hint="Names, aliases, or roles (comma-separated). Optional if anonymous.">
          <input
            value={form.involved}
            onChange={(e) => setF("involved", e.target.value)}
            placeholder="e.g. Peer A, Manager X"
            className={inputCls}
          />
        </FieldBlock>
        <FieldBlock isAnon={isAnon} label="Witnesses" hint="Optional, comma-separated">
          <input
            value={form.witnesses}
            onChange={(e) => setF("witnesses", e.target.value)}
            placeholder="e.g. Colleague B, Colleague C"
            className={inputCls}
          />
        </FieldBlock>
      </div>

      <FieldBlock isAnon={isAnon} label="What outcome are you seeking?" icon={Target}>
        <textarea
          rows={3}
          value={form.requested_outcome}
          onChange={(e) => setF("requested_outcome", e.target.value)}
          placeholder="e.g. Formal apology, mediated conversation, policy training…"
          className={inputCls}
          data-testid={TID.createCaseRequestedOutcome}
        />
      </FieldBlock>

      <FieldBlock isAnon={isAnon} label="Have you reported similar concerns before?" icon={History} hint="Optional context">
        <textarea
          rows={2}
          value={form.prior_reports}
          onChange={(e) => setF("prior_reports", e.target.value)}
          placeholder="e.g. Verbally raised with manager on Feb 4, no action taken."
          className={inputCls}
          data-testid={TID.createCasePriorReports}
        />
      </FieldBlock>
    </div>
  );
}

/* ============================================================================
   STEP 4 - Evidence & voice notes
   ============================================================================ */
function StepEvidence({
  attachments, uploadReason, setUploadReason, fileInputRef, handleFiles,
  startRec, stopRec, recording, recTime, uploading, removeAttachment, isAnon,
}) {
  const inputCls = "input";
  return (
    <div className="space-y-5">
      <div className={"border p-4 " + (isAnon ? "border-slate-800 bg-slate-900" : "border-blue-200 bg-blue-50")}>
        <div className="flex items-center gap-2 mb-1">
          <Fingerprint size={14} strokeWidth={2} className={isAnon ? "text-blue-300" : "text-blue-700"}/>
          <span className={"eyebrow " + (isAnon ? "text-blue-300" : "text-blue-700")}>Evidence integrity</span>
        </div>
        <p className={"text-xs leading-relaxed " + (isAnon ? "text-slate-300" : "text-blue-900")}>
          Every file is fingerprinted with SHA-256 and stamped with your reason for upload. Voice notes are transcribed by PropHR AI (Whisper) and preserved verbatim for the audit trail.
        </p>
      </div>

      <FieldBlock isAnon={isAnon} label="Reason for upload (required)" hint="Applies to the next files/voice notes you add">
        <input
          value={uploadReason}
          onChange={(e) => setUploadReason(e.target.value)}
          placeholder="e.g. Screenshot of the email exchange from March 12"
          className={inputCls}
          data-testid={TID.createCaseUploadReason}
        />
      </FieldBlock>

      <div className="flex flex-wrap gap-3">
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={TID.createCaseFileInput}/>
        <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-ghost" disabled={uploading}>
          <Paperclip size={14} strokeWidth={2}/> Attach files
        </button>
        {!recording ? (
          <button type="button" onClick={startRec} className="btn-ghost" data-testid={TID.createCaseRecordVoice}>
            <Mic size={14} strokeWidth={2}/> Record voice note
          </button>
        ) : (
          <button type="button" onClick={stopRec} className="btn-primary" data-testid={TID.createCaseStopVoice}>
            <Square size={14} strokeWidth={2}/> Stop · <span className="font-mono">{Math.floor(recTime/60)}:{String(recTime%60).padStart(2,"0")}</span>
          </button>
        )}
        {recording && <span className={"text-xs font-mono flex items-center gap-2 " + (isAnon ? "text-slate-300" : "text-red-600")}><AudioLines size={12} strokeWidth={2}/> Recording…</span>}
        {uploading && <span className="text-xs font-mono text-blue-600">Uploading…</span>}
      </div>

      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((a) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              className={"flex items-center justify-between border px-3 py-2 " + (isAnon ? "bg-slate-900 border-slate-700" : "bg-slate-50 border-slate-200")}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Paperclip size={12} strokeWidth={2} className={isAnon ? "text-slate-400" : "text-slate-500"}/>
                <div className="min-w-0">
                  <div className="text-sm truncate font-medium">{a.filename}</div>
                  <div className="text-[10px] font-mono opacity-60 flex items-center gap-2">
                    {a.kind.toUpperCase()} · {Math.round((a.size || 0)/1024)} KB
                    {a.transcription && " · transcribed"}
                  </div>
                  <div className="mt-1"><FingerprintChip fingerprint={a.fingerprint}/></div>
                  {a.reason && <div className="text-[10px] italic mt-1 opacity-70">"{a.reason}"</div>}
                </div>
              </div>
              <button type="button" onClick={() => removeAttachment(a.id)} className="opacity-60 hover:opacity-100 shrink-0"><X size={14}/></button>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className={"border border-dashed p-6 text-center text-xs " + (isAnon ? "border-slate-800 text-slate-500" : "border-slate-300 text-slate-500")}>
          No evidence attached yet. Files and voice notes are optional but strengthen a report.
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   STEP 5 - Consent (Anonymity toggle + preview of legal ack panel)
   ============================================================================ */
function StepConsent({ form, setF, toggleAnon, isAnon }) {
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => toggleAnon(false)}
          data-testid="consent-mode-identified"
          className={
            "text-left p-5 border transition-all " +
            (!isAnon
              ? "bg-blue-50 border-blue-600"
              : "bg-slate-900 border-slate-700 hover:border-slate-500")
          }
        >
          <div className="flex items-center gap-2 mb-2">
            <UserIcon size={16} strokeWidth={2} className={!isAnon ? "text-blue-700" : "text-slate-400"}/>
            <span className={"font-semibold text-sm " + (!isAnon ? "text-slate-900" : "text-slate-100")}>Report with my identity</span>
          </div>
          <p className={"text-xs leading-relaxed " + (!isAnon ? "text-slate-700" : "text-slate-400")}>
            Your name is visible to HR and (where relevant) your manager. Fastest path to resolution, and enables direct follow-up.
          </p>
        </button>

        <button
          type="button"
          onClick={() => toggleAnon(true)}
          data-testid={TID.createCaseAnonymous}
          className={
            "text-left p-5 border transition-all " +
            (isAnon
              ? "bg-slate-900 border-blue-500 text-slate-100"
              : "bg-white border-slate-200 hover:border-slate-400")
          }
        >
          <div className="flex items-center gap-2 mb-2">
            <EyeOff size={16} strokeWidth={2} className={isAnon ? "text-blue-300" : "text-slate-500"}/>
            <span className={"font-semibold text-sm " + (isAnon ? "text-blue-200" : "text-slate-900")}>Report anonymously</span>
          </div>
          <p className={"text-xs leading-relaxed " + (isAnon ? "text-slate-300" : "text-slate-600")}>
            Your identity is masked from HR and managers. Only Company Admin can lift the veil under legal process. Requires a typed legal signature and truthfulness acknowledgment.
          </p>
        </button>
      </div>

      {isAnon && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-blue-500/40 bg-blue-500/10 p-4 flex items-start gap-3"
        >
          <ShieldAlert size={18} strokeWidth={2} className="text-blue-300 shrink-0 mt-0.5"/>
          <div className="text-[13px] leading-relaxed text-blue-100">
            <div className="font-semibold mb-1">Anonymous reporting is a legal act.</div>
            You will be asked to type your full legal name as a signature on the next step. False reporting can result in disciplinary action, civil liability, and , where applicable , criminal charges under whistleblower fraud statutes. Your identity is stored securely and only accessible under legal process.
          </div>
        </motion.div>
      )}

      <FieldBlock isAnon={isAnon} label="Tags (optional)" hint="Free-form labels, comma-separated. Useful for filtering.">
        <input
          value={form.tags}
          onChange={(e) => setF("tags", e.target.value)}
          placeholder="e.g. remote, contract, safety"
          className="input"
        />
      </FieldBlock>
    </div>
  );
}

/* ============================================================================
   STEP 6 - Review, sign & submit
   ============================================================================ */
function StepReview({ form, user, setF, attachments, isAnon }) {
  const inputCls = "input";
  // Predict which workflow will be picked based on category.
  const predictedFlow = useMemo(() => {
    if (["Harassment", "Discrimination"].includes(form.category)) {
      return {
        name: "Harassment / Discrimination Investigation",
        steps: [
          { name: "Immediate Acknowledgment", role: "HR", sla: "4h" },
          { name: "Confidential Assignment", role: "HR", sla: "24h" },
          { name: "Interview & Evidence", role: "HR", sla: "10d" },
          { name: "Compliance Review", role: "Company Admin", sla: "4d" },
          { name: "Findings and Corrective Actions", role: "HR", sla: "48h" },
          { name: "Final Review", role: "Company Admin", sla: "24h" },
        ],
      };
    }
    if (form.category === "Whistleblower") {
      return {
        name: "Whistleblower Report",
        steps: [
          { name: "Sealed Intake", role: "Company Admin", sla: "8h" },
          { name: "Independent Investigation", role: "Company Admin", sla: "20d" },
          { name: "Audit Committee Review", role: "Super Admin", sla: "7d" },
          { name: "Findings and Remediation", role: "Company Admin", sla: "4d" },
        ],
      };
    }
    return {
      name: "Standard Case Investigation",
      steps: [
        { name: "Triage & Intake", role: "HR", sla: "24h" },
        { name: "Investigation", role: "HR", sla: "5d" },
        { name: "Findings Drafting", role: "HR", sla: "48h" },
        { name: "Findings Approval", role: "Company Admin", sla: "24h" },
      ],
    };
  }, [form.category]);

  return (
    <div className="space-y-6">
      <div className={"border p-5 " + (isAnon ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50")}>
        <div className="eyebrow mb-3">Review your report</div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Title" value={form.title || ","} isAnon={isAnon}/>
          <Row label="Category" value={form.category} isAnon={isAnon}/>
          <Row label="Priority" value={form.priority} mono isAnon={isAnon}/>
          <Row label="Impact" value={form.impact_severity} mono isAnon={isAnon}/>
          <Row label="Recurrence" value={form.recurrence.replaceAll("_", " ")} mono isAnon={isAnon}/>
          <Row label="Date" value={form.incident_date || ","} mono isAnon={isAnon}/>
          <Row label="Location" value={form.location || ","} isAnon={isAnon}/>
          <Row label="Involved" value={form.involved || ","} isAnon={isAnon}/>
          <Row label="Witnesses" value={form.witnesses || ","} isAnon={isAnon}/>
          <Row label="Attachments" value={`${attachments.length} file${attachments.length === 1 ? "" : "s"}`} mono isAnon={isAnon}/>
          <Row label="Reporting mode" value={isAnon ? "Anonymous" : "Identified"} mono isAnon={isAnon}/>
        </div>
        <div className={"mt-4 pt-3 border-t text-xs leading-relaxed " + (isAnon ? "border-slate-800 text-slate-300" : "border-slate-300 text-slate-700")}>
          <div className="eyebrow mb-1">Description</div>
          <div className="whitespace-pre-wrap max-h-40 overflow-auto pr-2">{form.description || ","}</div>
        </div>
      </div>

      {/* What happens after you submit */}
      <div className={"border p-5 " + (isAnon ? "border-blue-500/40 bg-blue-500/10" : "border-blue-200 bg-blue-50")}>
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck size={14} strokeWidth={2} className={isAnon ? "text-blue-300" : "text-blue-700"}/>
          <span className={"eyebrow " + (isAnon ? "text-blue-300" : "text-blue-700")}>What happens after you submit</span>
        </div>
        <p className={"text-[12px] mb-4 " + (isAnon ? "text-slate-300" : "text-blue-900")}>
          PropHR AI will summarize your report and route it through the workflow below. Every transition is fingerprinted and stored in a tamper-evident audit trail.
        </p>
        <div className={"font-semibold text-sm mb-3 " + (isAnon ? "text-slate-100" : "text-slate-900")}>
          Assigned workflow: {predictedFlow.name}
        </div>
        <div className="space-y-2">
          {predictedFlow.steps.map((s, i) => (
            <div key={s.name} className="flex items-center gap-3">
              <div className={"w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 " + (isAnon ? "bg-slate-800 border border-slate-700 text-slate-200" : "bg-white border border-slate-300 text-slate-700")}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={"text-sm font-medium " + (isAnon ? "text-slate-100" : "text-slate-900")}>{s.name}</div>
                <div className={"text-[10px] font-mono uppercase tracking-wider " + (isAnon ? "text-slate-500" : "text-slate-500")}>
                  {s.role} , SLA {s.sla}
                </div>
              </div>
              {i < predictedFlow.steps.length - 1 && (
                <ChevronRight size={12} className={isAnon ? "text-slate-600" : "text-slate-400"}/>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legal signature panel */}
      <div className={"border p-5 " + (isAnon ? "border-blue-500/50 bg-blue-500/5" : "border-slate-300 bg-white")}>
        <div className="flex items-center gap-2 mb-3">
          <PenLine size={16} strokeWidth={2} className={isAnon ? "text-blue-300" : "text-slate-800"}/>
          <span className={"eyebrow " + (isAnon ? "text-blue-300" : "")}>Signature & acknowledgment</span>
        </div>

        {isAnon ? (
          <div className="space-y-4">
            <div className={"border p-3 flex items-start gap-2 " + (isAnon ? "border-slate-800 bg-slate-900" : "border-amber-200 bg-amber-50")}>
              <AlertTriangle size={14} strokeWidth={2} className={isAnon ? "text-amber-300 mt-0.5 shrink-0" : "text-amber-700 mt-0.5 shrink-0"}/>
              <div className={"text-[12px] leading-relaxed " + (isAnon ? "text-slate-200" : "text-amber-900")}>
                <span className="font-semibold">Legal consequence acknowledgment.</span>{" "}
                By typing your full legal name below, you affirm , under penalty of policy violation and applicable law , that every statement in this report is true to the best of your knowledge. False reporting may result in disciplinary action up to termination and civil or criminal liability. Your signature is fingerprinted (SHA-256) with your IP address and user agent, and stored in a sealed record accessible only to Company Admin under legal process.
              </div>
            </div>

            <FieldBlock isAnon={isAnon} label={`Type your full legal name (must match "${user?.name || ""}")`}>
              <input
                value={form.legal_signature}
                onChange={(e) => setF("legal_signature", e.target.value)}
                placeholder={user?.name || "Full legal name"}
                className={inputCls + " font-editorial text-lg"}
                data-testid={TID.createCaseLegalSignature}
                autoComplete="off"
              />
            </FieldBlock>
          </div>
        ) : (
          <div className={"text-[12px] leading-relaxed " + (isAnon ? "text-slate-300" : "text-slate-700")}>
            You are submitting this report as <span className="font-semibold">{user?.name}</span> ({user?.email}). By checking the box below, you affirm the report is truthful.
          </div>
        )}

        <label className={"mt-4 flex items-start gap-3 cursor-pointer " + (isAnon ? "text-slate-100" : "text-slate-900")}>
          <input
            type="checkbox"
            checked={form.acknowledged_truthful}
            onChange={(e) => setF("acknowledged_truthful", e.target.checked)}
            className="mt-1 w-4 h-4"
            data-testid={TID.createCaseAckTruthful}
          />
          <span className="text-[13px] leading-relaxed">
            <span className="font-semibold">I acknowledge</span> that the information provided is truthful to the best of my knowledge, that PropHR AI outputs are advisory only, and that my organization's retention and privacy policies (GDPR, CCPA, EEOC, SOC 2) apply to this record.
          </span>
        </label>
      </div>
    </div>
  );
}

/* ============================================================================
   Small helpers
   ============================================================================ */
function FieldBlock({ label, hint, icon: Icon, isAnon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon size={13} strokeWidth={2} className={isAnon ? "text-slate-400" : "text-slate-500"}/>}
        <label className={"eyebrow " + (isAnon ? "text-slate-400" : "")}>{label}</label>
      </div>
      {children}
      {hint && <div className={"text-[11px] mt-1 " + (isAnon ? "text-slate-500" : "text-slate-500")}>{hint}</div>}
    </div>
  );
}

function Row({ label, value, mono, isAnon }) {
  return (
    <div>
      <div className={"text-[10px] font-mono uppercase tracking-wider mb-0.5 " + (isAnon ? "text-slate-500" : "text-slate-400")}>{label}</div>
      <div className={"break-words " + (mono ? "font-mono text-sm " : "text-sm ") + (isAnon ? "text-slate-100" : "text-slate-900")}>{value}</div>
    </div>
  );
}

function AnonymousDisclaimerModal({ onAccept, onCancel }) {
  const [agreed, setAgreed] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-950/85 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 12 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="w-full max-w-lg bg-slate-950 border border-slate-800 text-slate-100 p-6 incognito"
      >
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert size={18} strokeWidth={2} className="text-blue-300"/>
          <div className="font-display text-xl font-semibold">Anonymous reporting disclaimer</div>
        </div>
        <div className="space-y-3 text-[13px] leading-relaxed text-slate-300">
          <p>You are about to enable <span className="text-slate-100 font-semibold">Anonymous mode</span>. Please read carefully before proceeding:</p>
          <ul className="space-y-2 pl-4 list-disc">
            <li>Your identity will be masked from HR and managers. Only Company Admin can unmask under legal process.</li>
            <li>Your IP address, user-agent, and the SHA-256 fingerprint of your typed legal name will be recorded in a sealed audit record.</li>
            <li>Anonymous reporting is protected , but false, malicious, or knowingly untrue reporting may result in disciplinary action up to termination and civil or criminal liability.</li>
            <li>PropHR AI-generated summaries and risk scores are advisory only. All final decisions require human review.</li>
          </ul>
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid={TID.createCaseAnonDisclaimerAgree}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 w-4 h-4"
          />
          <span className="text-[13px]">I have read and understood the above disclaimer.</span>
        </label>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="btn-ghost !text-slate-300 hover:!bg-slate-800">Cancel</button>
          <button
            onClick={onAccept}
            disabled={!agreed}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="anon-disclaimer-continue"
          >
            <ClipboardCheck size={14} strokeWidth={2}/> Continue anonymously
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
